import React, { useEffect, useState } from 'react';
import { Canvas } from '@react-three/fiber';
import { useGLTF, Html, PerspectiveCamera } from '@react-three/drei';
import * as THREE from 'three';

const Phoenix = () => {
  const [isAnimating, setIsAnimating] = useState(false);

  return (
    <group>
      {/* Phoenix Body - Simplified with geometric shapes */}
      <mesh position={[0, 0, 0]}>
        <sphereGeometry args={[1, 32, 32]} />
        <meshStandardMaterial color="#FF4500" emissive="#FF8C00" emissiveIntensity={0.8} />
      </mesh>

      {/* Wings */}
      <mesh position={[-1.5, 0.5, 0]}>
        <boxGeometry args={[1.5, 2, 0.3]} />
        <meshStandardMaterial color="#DC143C" emissive="#FF6347" emissiveIntensity={0.7} />
      </mesh>
      <mesh position={[1.5, 0.5, 0]}>
        <boxGeometry args={[1.5, 2, 0.3]} />
        <meshStandardMaterial color="#DC143C" emissive="#FF6347" emissiveIntensity={0.7} />
      </mesh>

      {/* Tail */}
      <mesh position={[0, -0.5, -2]}>
        <coneGeometry args={[0.8, 2, 32]} />
        <meshStandardMaterial color="#FFD700" emissive="#FFA500" emissiveIntensity={0.8} />
      </mesh>

      {/* Flame aura */}
      <mesh position={[0, -1.5, 0]}>
        <cylinderGeometry args={[2.5, 3, 0.5, 32]} />
        <meshStandardMaterial
          color="#FF4500"
          emissive="#FF8C00"
          emissiveIntensity={0.6}
          transparent
          opacity={0.7}
        />
      </mesh>
    </group>
  );
};

interface PhoenixTitleProps {
  onAnimationComplete?: () => void;
  playAnimation?: boolean;
}

export const PhoenixTitle: React.FC<PhoenixTitleProps> = ({ onAnimationComplete, playAnimation = false }) => {
  const [displayText, setDisplayText] = useState('');
  const targetText = 'Dark Hells';

  useEffect(() => {
    if (!playAnimation) return;

    let index = 0;
    const interval = setInterval(() => {
      if (index <= targetText.length) {
        setDisplayText(targetText.slice(0, index));
        index++;
      } else {
        clearInterval(interval);
        setTimeout(() => onAnimationComplete?.(), 1000);
      }
    }, 100);

    return () => clearInterval(interval);
  }, [playAnimation, onAnimationComplete]);

  return (
    <div className="relative w-full h-full flex flex-col items-center justify-center">
      <Canvas camera={{ position: [0, 2, 5], fov: 50 }}>
        <PerspectiveCamera makeDefault position={[0, 2, 5]} />
        <ambientLight intensity={0.8} />
        <pointLight position={[10, 10, 10]} intensity={1} color="#FFD700" />
        <pointLight position={[-10, 5, -10]} intensity={0.5} color="#FF4500" />
        <Phoenix />
      </Canvas>

      {playAnimation && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="text-6xl font-bold text-transparent bg-gradient-to-r from-orange-500 via-red-600 to-yellow-500 bg-clip-text animate-pulse">
            {displayText}
          </div>
        </div>
      )}

      {/* Smoke effect below */}
      <div className="absolute bottom-10 w-32 h-32 pointer-events-none">
        <div className="animate-pulse w-full h-full rounded-full bg-gradient-to-t from-gray-600 to-transparent opacity-40" />
      </div>
    </div>
  );
};
